import React, {
  useEffect,
  useCallback,
  useMemo,
  useState,
  useRef,
} from "react";
import Button from "../Buttons/Button";
import DropdownMenu from "../Buttons/DropdownMenu";
import { useNavigate } from "react-router-dom";

const PageTab = ({
  tabs,
  mainTabsShow = 0,
  defaultTab = 0,
  color = "primary",
  activeColor = "primary",
  variant = "contained",
  activeVariant = "contained",
  onTabChange, // new
}) => {
  const navigate = useNavigate();
  const tabContainerRef = useRef(null);
  const [visibleTabs, setVisibleTabs] = useState(tabs.length);

  const [activeMainTab, setActiveMainTab] = useState(defaultTab);
  const [activeSubTab, setActiveSubTab] = useState(null);
  const [isMoreMenuActive, setIsMoreMenuActive] = useState(false);

  const calculateVisibleTabs = useCallback(() => {
    if (!tabContainerRef.current) return;

    const container = tabContainerRef.current;
    const containerWidth = container.offsetWidth;
    const tabElements = container.querySelectorAll(".main-tab-button");

    let totalWidth = 0;
    let visibleCount = tabElements.length;

    tabElements.forEach((tab, index) => {
      totalWidth += tab.offsetWidth + 8; // 8px for gap
      if (totalWidth > containerWidth - 100) {
        // 100px reserved for more button
        visibleCount = index;
        return;
      }
    });

    setVisibleTabs(visibleCount);
  }, []);

  useEffect(() => {
    const resizeObserver = new ResizeObserver(calculateVisibleTabs);
    if (tabContainerRef.current) {
      resizeObserver.observe(tabContainerRef.current);
    }
    return () => resizeObserver.disconnect();
  }, [calculateVisibleTabs]);

  // Modify the useMemo to use visibleTabs
  const { findMainTabs, mainTabs, mainMore, subTabs, showMore } =
    useMemo(() => {
      const findMainTabs = tabs.filter((tab) => tab.type === "main");
      const effectiveTabCount =
        mainTabsShow === 0 ? visibleTabs : Math.min(mainTabsShow, visibleTabs);
      const mainTabs = findMainTabs.slice(0, effectiveTabCount);
      const mainMore = findMainTabs.slice(effectiveTabCount);
      //ss
      const activeMainTabData = findMainTabs[activeMainTab];
      const subTabs =
        activeMainTabData?.subTabs ||
        tabs.filter((tab) => tab.type === "sub") ||
        [];

      // const subTabs = tabs.filter((tab) => tab.type === "sub");
      const showMore = mainMore.length > 0;

      return { findMainTabs, mainTabs, mainMore, subTabs, showMore };
    }, [tabs, mainTabsShow, visibleTabs, activeMainTab]);

  const handleTabClick = useCallback(
    (item, index, isMainTab = true) => {
      if (isMainTab) {
        // For main tabs, use the index directly
        setActiveMainTab(index);
        setActiveSubTab(null);
        if (onTabChange) {
          onTabChange(index);
        }
      } else {
        setActiveSubTab(index);
      }
      if (item.action) {
        item.action();
      }
      if (item.doubleAction) {
        item.doubleAction();
      }
      if (item.link) {
        navigate(item.link);
      }
    },
    [navigate, onTabChange]
  );

  useEffect(() => {
    // Check if the active tab is in the mainMore array
    const isInMoreMenu = activeMainTab >= mainTabs.length;
    setIsMoreMenuActive(isInMoreMenu);
  }, [activeMainTab, mainTabs.length]);

  return (
    <div
      role="tablist"
      className="tab-container flex flex-1 flex-col overflow-hidden"
    >
      <div className="flex justify-between items-end gap-10 border-b-[1px]  border-gray-200">
        <div ref={tabContainerRef} className="flex gap-2 items-end flex-1">
          {/* Main tabs remain the same */}
          {mainTabs.map((item, key) => (
            <Button
              key={key}
              className="main-tab-button"
              type="tab"
              role="tab"
              aria-selected={key === activeMainTab}
              color={key === activeMainTab ? activeColor : color}
              variant={key === activeMainTab ? activeVariant : variant}
              title={item.title}
              icon={item.icon && item.icon}
              iconPosition={item.iconPosition && item.iconPosition}
              size="sm"
              onClick={() => handleTabClick(item, key)}
              active={
                key === activeMainTab &&
                (activeColor === color || variant === activeVariant)
              }
              disabled={item.disabled}
              value={item.value && item.value}
            />
          ))}

          {showMore && (
            <DropdownMenu
              MenuSection={
                <div className="p-1 flex flex-col gap-0.5">
                  {mainMore.map((item, key) => (
                    <div key={key} className="w-full">
                      <Button
                        title={item.title}
                        icon={item.icon}
                        variant="text"
                        color={
                          mainTabs.length + key === activeMainTab
                            ? activeColor
                            : color
                        }
                        size="sm"
                        className="w-full"
                        onClick={() => {
                          // Calculate the correct index for items in the More menu
                          const actualIndex = mainTabs.length + key;
                          handleTabClick(item, actualIndex);
                        }}
                        active={mainTabs.length + key === activeMainTab}
                        fromLeft
                      />
                    </div>
                  ))}
                </div>
              }
            >
              <Button
                type="tab"
                size="sm"
                title={`More`}
                color={isMoreMenuActive ? activeColor : color}
                variant={isMoreMenuActive ? activeVariant : variant}
                icon="keyboard_arrow_down"
                iconPosition="after"
              />
            </DropdownMenu>
          )}
        </div>

        <div className="sub-tabs flex gap-2 items-end">
          {subTabs.map((item, key) => (
            <Button
              key={key}
              type="tab"
              role="tab"
              aria-selected={key === activeSubTab}
              color={`${variant === "text" ? "primary" : "black"}`}
              title={item.title}
              icon={item.icon}
              size="sm"
              onClick={() => handleTabClick(item, key, false)}
              variant={variant === "text" ? "text" : "contained"}
              active={key === activeSubTab}
            />
          ))}
        </div>
      </div>

      <div role="tabpanel" className="tab-content flex flex-1 overflow-hidden">
        {findMainTabs[activeMainTab]?.page && (
          <div className="flex flex-1 flex-col overflow-auto">
            {React.cloneElement(findMainTabs[activeMainTab].page, {
              setActiveTab: setActiveMainTab,
            })}
          </div>
        )}
        {activeSubTab !== null && subTabs[activeSubTab]?.page && (
          <div>{subTabs[activeSubTab].page}</div>
        )}
      </div>
    </div>
  );
};

export default PageTab;
